/**
 * @file 文档菜单hook
 * @author Wanzhongyun
 */
import { useCallback, useState } from 'react';
import { useLatest } from 'react-use';

import useDocTopMenu, { getTopMenuList } from './components/doc-top-menu/useDocTopMenu';
import useDocRightMenu, { getRightMenuList } from './components/doc-right-menu/useDocRightMenu';
import useDocLeftMenu, { getLeftMenuList } from './components/doc-left-menu/useDocLeftMenu';

import { IDocumentMenuItem } from './type';

const useDocMenu = (isPage: boolean) => {
    const [menuList, setMenuList] = useState<IDocumentMenuItem[]>([]);
    const docTopMenuHook = useDocTopMenu();
    const docRightMenuHook = useDocRightMenu();
    const docLeftMenuHook = useDocLeftMenu();

    const { setMenuList: setTopDocMenuList, setSelectedKeys: setTopDocMenuSelectedKeys } = docTopMenuHook;
    const { setMenuList: setLeftDocMenuList, setSelectedKeys: setLeftDocMenuSelectedKeys } = docLeftMenuHook;
    const { setMenuList: setRightDocMenuList, setSelectedKeys: setRightDocMenuSelectedKeys } = docRightMenuHook;

    const updateMenuList = useCallback(
        (menuList: IDocumentMenuItem[], selectedKeys?: string[]) => {
            setMenuList(menuList);

            if (selectedKeys) {
                setTopDocMenuSelectedKeys(selectedKeys);
                setLeftDocMenuSelectedKeys(selectedKeys);
                setRightDocMenuSelectedKeys(selectedKeys);
            }
            setTopDocMenuList(getTopMenuList(menuList));
            setLeftDocMenuList(getLeftMenuList(menuList, selectedKeys || [], isPage));
            setRightDocMenuList(getRightMenuList(menuList, selectedKeys || []));
        },
        [
            setMenuList,
            setTopDocMenuList,
            setTopDocMenuSelectedKeys,
            setLeftDocMenuList,
            setLeftDocMenuSelectedKeys,
            setRightDocMenuList,
            setRightDocMenuSelectedKeys,
            isPage
        ]
    );

    const menuListRef = useLatest(menuList);
    const onTopMenuChange = useCallback(
        (keys: string[]) => {
            const leftMenuList = getLeftMenuList(menuListRef.current, keys || [], isPage);
            setTopDocMenuSelectedKeys(keys);
            setLeftDocMenuSelectedKeys(keys);
            setLeftDocMenuList(leftMenuList);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuListRef, setLeftDocMenuList, setTopDocMenuSelectedKeys, setLeftDocMenuSelectedKeys]
    );

    return {
        updateMenuList,
        onTopMenuChange,

        menu: {
            menuList,
            setMenuList
        },
        top: docTopMenuHook,
        right: docRightMenuHook,
        left: docLeftMenuHook
    };
};

export default useDocMenu;
